import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { ProgressFallback } from "src/ui/components/shared/system/ProgressFallback";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "src/hooks/utils/useLocalStorage";
import { IntercomProps, useIntercom } from "react-use-intercom";
import { CUSTOM_LAUNCHER_SELECTOR } from "src/constants/intercom";
import {
  AuthWhoAmIRes,
  BasicOrgAccessInfo,
  BasicWorkspaceAccessInfo,
  N_UserProfile,
  ORGANISATION_PERMISSIONS,
  orgMemberRole,
  orgMemberStatus,
  routePaths,
  socketMessageKinds,
  WhoAmIReq,
  WORKSPACE_PERMISSIONS,
  workspaceMemberRole,
  UserOnboardingRes,
  idFlavor,
} from "@specsheet-common/shared-types";
import { useWebsocket } from "src/hooks/contexts/useWebsocket";
import { useLocationState } from "src/hooks/utils/useLocationState";
import { useApplicationVersionContext } from "../hooks/contexts/useApplicationVersionContext";
import * as datadog from "src/util/datadog";
import { useWhoAmI } from "../service/queryHooks/auth/mutations/useWhoAmI";
import { useAuth0 } from "@auth0/auth0-react";
import { useAccessToken } from "src/hooks/utils/useAccessToken";
import { useApolloClient } from "@apollo/client";
import { useActivateUser } from "../service/queryHooks/auth/mutations/useActivateUser";
import {
  clearAllQueryCaches,
  createCampaignPermissionCacheGuard,
} from "src/service/reactQuery/dropStaleCampaignPermissionCaches";

export type AuthOrganisation = Omit<BasicOrgAccessInfo, "permissions"> & {
  permissions: Set<ORGANISATION_PERMISSIONS>;
};

export type AuthWorkspace = Omit<BasicWorkspaceAccessInfo, "permissions"> & {
  permissions: Set<WORKSPACE_PERMISSIONS>;
};

interface State {
  isAuthenticated: boolean;
  isInitialised: boolean;
  hasWorkspaceAccess: boolean;
  profile: N_UserProfile;
  organisation: AuthOrganisation;
  workspace: AuthWorkspace;
  isOpsManager: boolean;
  isInternalUser: boolean;
  allOrgs: BasicOrgAccessInfo[];
}

const EMPTY_WORKSPACE = {
  id: "",
  name: "",
  logo: "",
  isPaid: true,
  role: workspaceMemberRole.GUEST,
  permissions: new Set<WORKSPACE_PERMISSIONS>(),
};

const EMPTY_ORGANISATION = {
  id: "noActiveOrg",
  name: "No active organisations",
  logo: "",
  config: null,
  role: orgMemberRole.BASIC,
  status: orgMemberStatus.Removed,
  permissions: new Set<ORGANISATION_PERMISSIONS>(),
  orgMemberId: "",
  hidden: true,
};

const initialAuthState: State = {
  isAuthenticated: false,
  isInitialised: false,
  profile: {
    userId: "",
    profilePic: "",
    lastName: "",
    name: "",
    email: "",
    requiresOnboarding: false,
  },
  organisation: EMPTY_ORGANISATION,
  workspace: EMPTY_WORKSPACE,
  isOpsManager: false,
  isInternalUser: false,
  hasWorkspaceAccess: false,
  allOrgs: [],
};

type ReducerAction =
  | {
      payload:
        | {
            isAuthenticated: true;
            whoAmIData: AuthWhoAmIRes;
          }
        | { isAuthenticated: false };
      type: "INITIALISE";
    }
  | {
      payload: {
        profile: N_UserProfile;
      };
      type: "UPDATE_SELF_DETAILS";
    }
  | {
      payload?: never;
      type: "LOGOUT";
    }
  | {
      type: "FINALISE_ONBOARDING";
      payload: UserOnboardingRes;
    };

const populateWorkspaceWithPermissions = (
  workspace: BasicWorkspaceAccessInfo
): State["workspace"] => {
  return { ...workspace, permissions: new Set(workspace.permissions) };
};

const populateOrganisationWithPermissions = (
  org?: BasicOrgAccessInfo
): State["organisation"] => {
  return org
    ? { ...org, permissions: new Set(org.permissions) }
    : EMPTY_ORGANISATION;
};

const reducer = (state: State, action: ReducerAction): State => {
  switch (action.type) {
    case "INITIALISE": {
      const { isAuthenticated } = action.payload;

      if (!action.payload.isAuthenticated) {
        return {
          ...initialAuthState,
          isInitialised: true,
        };
      }

      const {
        workspace,
        organisation,
        userProfile,
        isOpsManager,
        isInternalUser,
        allOrgs,
      } = action.payload.whoAmIData;

      if (!workspace) {
        return {
          ...state,
          isInitialised: true,
          isAuthenticated,
          organisation: populateOrganisationWithPermissions(organisation),
          profile: userProfile,
          isOpsManager: Boolean(isOpsManager),
          isInternalUser: Boolean(isInternalUser),
          allOrgs,
        };
      }

      return {
        ...state,
        isInitialised: true,
        isAuthenticated,
        workspace: populateWorkspaceWithPermissions(workspace),
        organisation: populateOrganisationWithPermissions(organisation),
        profile: userProfile,
        isOpsManager: Boolean(isOpsManager),
        isInternalUser: Boolean(isInternalUser),
        allOrgs,
      };
    }
    case "LOGOUT": {
      return {
        ...initialAuthState,
        isInitialised: true,
        isAuthenticated: false,
      };
    }
    case "UPDATE_SELF_DETAILS": {
      const { profile } = action.payload;

      return {
        ...state,
        profile,
      };
    }
    case "FINALISE_ONBOARDING": {
      const data = action.payload;
      return {
        ...state,
        profile: {
          ...state.profile,
          name: data.name,
          lastName: data.lastName,
          requiresOnboarding: false,
        },
      };
    }
    default: {
      return { ...state };
    }
  }
};

const getIntercomParams = (
  userAccess?: AuthWhoAmIRes
): Partial<IntercomProps> => ({
  ...(userAccess === undefined
    ? {}
    : {
        userId: userAccess.userProfile.userId,
        name: `${userAccess.userProfile.name} ${userAccess.userProfile.lastName}`,
        email: userAccess.userProfile.email,
        company: {
          companyId: userAccess.organisation?.id ?? "noActiveOrg",
          name: userAccess.organisation?.name ?? "No Active Org",
          customAttributes: {
            workspaceId: userAccess.workspace?.id,
            workspaceName: userAccess.workspace?.name,
          },
        },
      }),
  customLauncherSelector: CUSTOM_LAUNCHER_SELECTOR,
  hideDefaultLauncher: true,
});

const useUserUpdatedListener = ({ reinitialise }: { reinitialise(): void }) => {
  const { socket } = useWebsocket();

  const websocketCallback = useCallback(() => {
    reinitialise();
  }, [reinitialise]);

  useEffect(() => {
    socket?.on(socketMessageKinds.userUpdated, websocketCallback);
    return () => {
      socket?.off(socketMessageKinds.userUpdated, websocketCallback);
    };
  }, [socket, websocketCallback]);
};

type AuthContextProps = State & {
  logout(): void;
  reinitialise(): void;
  finaliseOnboarding(data: UserOnboardingRes): void;
};

export const AuthContext = createContext<AuthContextProps | null>(null);

export const AuthProvider: React.FC = ({ children }) => {
  const {
    isAuthenticated: isAuthenticated0,
    user,
    logout: auth0Logout,
  } = useAuth0();
  const { getAccessToken } = useAccessToken();
  const client = useApolloClient();
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const navigate = useNavigate();
  const { setVersion } = useApplicationVersionContext();
  const [savedOrganisationId, setSavedOrganisationId] = useLocalStorage<
    string | null
  >({
    key: "organisation-id",
    defaultValue: null,
  });

  const [savedWorkspaceId, setSavedWorkspaceId] = useLocalStorage<
    string | null
  >({
    key: "workspace-id",
    defaultValue: null,
  });
  const { update: updateIntercom } = useIntercom();
  const { wsEmit } = useWebsocket();
  const locationState = useLocationState();
  const campaignPermissionCacheGuard = useMemo(
    () => createCampaignPermissionCacheGuard(),
    []
  );

  const logout = useCallback(() => {
    wsEmit[socketMessageKinds.userUpdated](state.profile.userId);
    dispatch({ type: "LOGOUT" });
    // Drop React Query cache so the next account cannot inherit
    // prior campaignPermissions / userAccessLevel.
    clearAllQueryCaches();
    campaignPermissionCacheGuard.reset();
    auth0Logout();
    void client.resetStore();
    navigate(routePaths.login);
  }, [
    auth0Logout,
    campaignPermissionCacheGuard,
    client,
    navigate,
    state.profile.userId,
    wsEmit,
  ]);

  const whoAmIHook = useWhoAmI({ onError: logout });
  const activateUserHook = useActivateUser();

  const doLogin = async (authToken: idFlavor<"AuthToken">) => {
    wsEmit[socketMessageKinds.clientLogin](authToken);

    const [whoAmIRes] = await Promise.all([
      whoAmIHook.mutateAsync(
        savedOrganisationId
          ? {
              workspaceId: savedWorkspaceId || undefined,
              organisationId: savedOrganisationId,
            }
          : undefined
      ),
      activateUserHook.mutateAsync(),
    ]);

    updateIntercom(getIntercomParams(whoAmIRes));
    setVersion(whoAmIRes.applicationVersion);

    campaignPermissionCacheGuard.onAuthContext({
      userId: whoAmIRes.userProfile.userId,
      organisationId: whoAmIRes.organisation?.id ?? "",
      workspaceId: whoAmIRes.workspace?.id ?? "",
    });

    dispatch({
      type: "INITIALISE",
      payload: { isAuthenticated: true, whoAmIData: whoAmIRes },
    });
  };

  const finaliseOnboarding = (data: UserOnboardingRes) => {
    try {
      dispatch({
        type: "FINALISE_ONBOARDING",
        payload: data,
      });
    } catch {}
  };

  const initialise = useCallback(
    async (data: WhoAmIReq) => {
      const accessToken = isAuthenticated0 ? await getAccessToken() : undefined;
      try {
        if (accessToken) {
          try {
            const reqData: WhoAmIReq =
              data ||
              (savedOrganisationId
                ? {
                    workspaceId: savedWorkspaceId || undefined,
                    organisationId: savedOrganisationId,
                  }
                : undefined);

            const whoAmIRes = await whoAmIHook.mutateAsync(reqData);
            updateIntercom(getIntercomParams(whoAmIRes));
            setVersion(whoAmIRes.applicationVersion);

            campaignPermissionCacheGuard.onAuthContext({
              userId: whoAmIRes.userProfile.userId,
              organisationId: whoAmIRes.organisation?.id ?? "",
              workspaceId: whoAmIRes.workspace?.id ?? "",
            });

            dispatch({
              type: "INITIALISE",
              payload: {
                isAuthenticated: true,
                whoAmIData: whoAmIRes,
              },
            });
          } catch (e) {
            datadog.error(e);
          }
        } else {
          updateIntercom(getIntercomParams());

          dispatch({
            type: "INITIALISE",
            payload: {
              isAuthenticated: false,
            },
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        datadog.error(err);
        dispatch({
          type: "INITIALISE",
          payload: {
            isAuthenticated: false,
          },
        });
      }
    },
    [
      campaignPermissionCacheGuard,
      getAccessToken,
      isAuthenticated0,
      savedOrganisationId,
      savedWorkspaceId,
      setVersion,
      updateIntercom,
      whoAmIHook,
    ]
  );

  const reinitialise = useCallback(async () => {
    await initialise(undefined);
  }, [initialise]);

  useEffect(() => {
    void reinitialise();
  }, []);
  useUserUpdatedListener({ reinitialise });

  useEffect(() => {
    if (locationState.workspaceId) {
      setSavedWorkspaceId(locationState.workspaceId);
    }
    if (locationState.organisationId) {
      setSavedOrganisationId(locationState.organisationId);
    }
    if (locationState.workspaceId || locationState.organisationId) {
      const fallbackOrganisation =
        state.organisation.id === "" ? undefined : state.organisation.id;
      void initialise({
        workspaceId: locationState.workspaceId || undefined,
        organisationId: locationState.organisationId || fallbackOrganisation,
      });
    }
  }, [locationState.workspaceId, locationState.organisationId]);

  useEffect(() => {
    if (isAuthenticated0 && user?.email && !state.isAuthenticated) {
      void getAccessToken().then(doLogin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated0]);

  if (!state.isInitialised) {
    return <ProgressFallback />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        reinitialise,
        logout,
        finaliseOnboarding,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
